import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { externalRedirect } from '@americanexpress/one-app-ducks';
import { composeModules, RenderModule } from 'holocron';
import { createStructuredSelector } from 'reselect';
import { getActiveLocaleSelector, getEnvironmentSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { useFeatureFlag } from '@americanexpress/one-experiments';
import hostSelector from '../selectors/hostSelector';
import { ACCOUNT_SELECTION_MODAL } from '../constants/accountSelectionFeatureRule';
import { useCmsFeatureFlag } from '../hooks/useCmsFeatureFlag';
import { isCmsFlagEnabledThunk } from '../thunks/cmsFeatureFlagsThunks';

const PageWrapper = ({
  host,
  location: {
    pathname,
  },
  route: {
    hideGlobalHeader,
    hideTravelNavigation,
    hideGlobalFooter,
  },
  children,
}) => {
  const {
    enabled: modalEnabled,
    properties: modalProperties,
  } = useFeatureFlag({ featureFlagName: 'prebooking_horizons_card_selection_modal' });

  const {
    isLoading: featureFlagLoading,
    isEnabled: showTravelHeader,
  } = useCmsFeatureFlag('travel_header');

  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const shouldRenderCardSelectionAsModal = modalEnabled
    && modalProperties?.[tenancy]?.includes(locale);

  const shouldOnlyRenderChildren = !shouldRenderCardSelectionAsModal
    && hideGlobalFooter
    && hideGlobalHeader
    && hideTravelNavigation;

  if (shouldOnlyRenderChildren) {
    return children;
  }

  const destPage = `https://${host}${pathname}`;

  if (featureFlagLoading) {
    return null;
  }

  return (
    <div className="body">
      { shouldRenderCardSelectionAsModal && (
        <RenderModule
          moduleName="axp-travel-account-selection"
          props={{ shouldRenderAsFullPageModal: true }}
        />
      ) }
      { !hideGlobalHeader && (
      <div className={`${!hideTravelNavigation && 'hidden-md-down'}`}>
        <RenderModule
          moduleName="axp-global-header"
          props={{
            loginDestPage: destPage,
            logoutDestPage: destPage,
          }}
        />
      </div>
      )}
      { !hideTravelNavigation
        && <RenderModule moduleName={showTravelHeader ? 'travel-header' : 'axp-travel-navigation'} /> }
      { /* eslint-disable-next-line no-extra-parens -- required for ternary */ }
      <main id="mainContent" style={{ paddingTop: `${hideTravelNavigation ? '' : (showTravelHeader ? '3.5rem' : '3.125rem')}` }}>
        {children}
      </main>
      { !hideGlobalFooter
        && <RenderModule moduleName="axp-footer" /> }
    </div>
  );
};

PageWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  host: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  route: PropTypes.shape({
    hideGlobalHeader: PropTypes.bool,
    hideTravelNavigation: PropTypes.bool,
    hideGlobalFooter: PropTypes.bool,
  }),
  params: PropTypes.shape({
    locale: PropTypes.string,
  }),
};

PageWrapper.defaultProps = {
  route: {
    hideGlobalHeader: false,
    hideTravelNavigation: false,
    hideGlobalFooter: false,
  },
  params: {
    locale: '',
  },
};

export const loadModuleData = async ({
  store: { getState, dispatch },
  ownProps,
}) => {
  const state = getState();
  const locale = getActiveLocaleSelector(state);
  const environment = getEnvironmentSelector(state);
  const tenancy = getTenancySelector(state);

  const isAccountModalEnabled = ACCOUNT_SELECTION_MODAL[tenancy]?.[environment]?.includes(locale)
    || false;

  await dispatch(isCmsFlagEnabledThunk('travel_header'));

  return dispatch(composeModules([
    ...isAccountModalEnabled ? [
      {
        name: 'axp-travel-account-selection',
        props: {
          shouldRenderAsFullPageModal: true,
        },
      },
    ] : [],
    ...ownProps?.route?.hideGlobalHeader ? [] : [{ name: 'axp-global-header' }],
    ...ownProps?.route?.hideTravelNavigation ? [] : [{ name: 'axp-travel-navigation' }],
    ...ownProps?.route?.hideTravelNavigation ? [] : [{ name: 'travel-header' }],
    ...ownProps?.route?.hideGlobalFooter ? [] : [{ name: 'axp-footer' }],
  ]));
};

export const mapStateToProps = createStructuredSelector({
  host: hostSelector,
});

const mapDispatchToProps = {
  redirect: externalRedirect,
};

const hocChain = compose(
  oneAppModuleWrapper('axp-travel-page-wrapper'),
  connect(mapStateToProps, mapDispatchToProps)
);

export { PageWrapper as TestablePageWrapper };

PageWrapper.holocron = {
  name: 'axp-travel-page-wrapper',
  loadModuleData,
};

export default hocChain(PageWrapper);
